import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import Close from '@engineering/icons/close';
import type { OverlaySize, OverlayTitlePosition } from './types';
import {
  Backdrop,
  Wrapper,
  Content,
  TitleWrapper,
  Title,
  ScrollableContent,
  StyledIconButton,
  Footer,
} from './Overlay.styles';

// Checkpoints (ms after mount) sampled to detect a sustained narrow render
// of a full-viewport overlay, rather than a single-frame flash. See WWW-3521.
const NARROW_RENDER_CHECK_DELAYS_MS = [0, 150, 400];
const NARROW_RENDER_WIDTH_RATIO_THRESHOLD = 0.8;
const NARROW_RENDER_MIN_SAMPLES = 2;

type NarrowRenderInfo = {
  /** Rendered width of the Content box — the element that collapses. */
  renderedWidth: number;
  /** `window.innerWidth` read at the same checkpoint. */
  viewportWidth: number;
  /**
   * Rendered width of the `position: fixed; inset: 0` Wrapper that Content is
   * a flex item of. If this collapsed too, the fault is above Content; if it
   * is full width while Content is not, Content itself was shrunk.
   */
  wrapperWidth: number | null;
  /**
   * Width of a throwaway probe element carrying the same `width: 100vw`
   * Content relies on. This is what the `vw` unit actually resolved to at
   * that moment, which separates a bad viewport unit from a bad box/flex
   * computation — the two remaining explanations for WWW-3521.
   */
  vwProbeWidth: number;
  /** `document.documentElement.clientWidth` at the same checkpoint. */
  documentClientWidth: number;
  /** `visualViewport` width/scale, when the browser exposes the API. */
  visualViewportWidth: number | null;
  visualViewportScale: number | null;
  /** Path only — never the query string, which can carry campaign/PII data. */
  path: string;
};

type Props = PropsWithChildren<{
  id?: string;
  title?: ReactNode;
  titlePosition?: OverlayTitlePosition;
  footerContent?: ReactNode;
  closeLabel?: string;
  open: boolean;
  width?: OverlaySize;
  withoutPadding?: boolean;
  centered?: boolean;
  onClose: () => void;
  /**
   * Called at most once if a full-viewport overlay renders noticeably
   * narrower than the viewport across several checkpoints after mount.
   * Only observed for `width="full"`. See WWW-3521.
   */
  onNarrowRender?: (info: NarrowRenderInfo) => void;
}>;

const Overlay = ({
  id,
  title,
  titlePosition = 'auto',
  footerContent,
  closeLabel = 'close', // you should set this to the correct translation
  children,
  open,
  width = 'normal',
  withoutPadding = false,
  centered = false,
  onClose,
  onNarrowRender,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Held in a ref so that a caller passing an inline callback cannot restart
  // the checkpoint timers on every re-render, which would discard samples and
  // silently prevent the measurement from ever completing.
  const onNarrowRenderRef = useRef(onNarrowRender);

  useEffect(() => {
    onNarrowRenderRef.current = onNarrowRender;
  }, [onNarrowRender]);

  useEffect(() => {
    if (!open || width !== 'full' || !onNarrowRenderRef.current) {
      return undefined;
    }

    let cancelled = false;
    const samples: NarrowRenderInfo[] = [];

    // A `100vw` probe, clipped inside a 0x0 container so it can never add
    // overflow or a scrollbar of its own. Its laid-out width still reflects
    // whatever `100vw` currently resolves to.
    const probeClip = document.createElement('div');
    probeClip.setAttribute('aria-hidden', 'true');
    probeClip.style.cssText =
      'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none';
    const probe = document.createElement('div');
    probe.style.cssText = 'width:100vw;height:0';
    probeClip.appendChild(probe);
    document.body.appendChild(probeClip);

    const report = () => {
      const narrowSamples = samples.filter(
        sample =>
          sample.renderedWidth / sample.viewportWidth <
          NARROW_RENDER_WIDTH_RATIO_THRESHOLD,
      );
      if (narrowSamples.length >= NARROW_RENDER_MIN_SAMPLES) {
        const [firstSample, ...restSamples] = narrowSamples;
        const narrowestSample = restSamples.reduce(
          (narrowest, sample) =>
            sample.renderedWidth < narrowest.renderedWidth ? sample : narrowest,
          firstSample,
        );
        onNarrowRenderRef.current?.(narrowestSample);
      }
    };

    const lastCheckpoint = NARROW_RENDER_CHECK_DELAYS_MS.length - 1;
    const timers = NARROW_RENDER_CHECK_DELAYS_MS.map((delay, checkpoint) =>
      window.setTimeout(() => {
        if (cancelled) return;

        // Headless UI mounts the dialog into a portal only after this effect
        // has run, so the node is read per checkpoint instead of captured up
        // front — at mount time it is not attached yet. A checkpoint that
        // finds no node is skipped, and the verdict is reached on the last
        // checkpoint regardless of how many samples were collected.
        const node = contentRef.current;
        if (node) {
          const { visualViewport } = window;
          samples.push({
            renderedWidth: node.getBoundingClientRect().width,
            viewportWidth: window.innerWidth,
            wrapperWidth:
              wrapperRef.current?.getBoundingClientRect().width ?? null,
            vwProbeWidth: probe.getBoundingClientRect().width,
            documentClientWidth: document.documentElement.clientWidth,
            visualViewportWidth: visualViewport?.width ?? null,
            visualViewportScale: visualViewport?.scale ?? null,
            path: window.location.pathname,
          });
        }

        if (checkpoint === lastCheckpoint) report();
      }, delay),
    );

    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
      probeClip.remove();
    };
  }, [open, width]);

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogBackdrop as={Backdrop} />
      <Wrapper
        ref={wrapperRef}
        role="presentation"
        id={id}
        $width={width}
        $centered={!!centered}
      >
        <DialogPanel
          as={Content}
          ref={contentRef}
          role="presentation"
          $fullViewport={width === 'full'}
          $rounded={!!centered}
        >
          <TitleWrapper $hasTitle={!!title}>
            {title && (
              <DialogTitle as="div">
                <Title $titlePosition={titlePosition}>{title}</Title>
              </DialogTitle>
            )}
            <CloseButton
              as={StyledIconButton}
              icon={Close}
              title={closeLabel}
            />
          </TitleWrapper>
          <ScrollableContent
            role="presentation"
            $fullViewport={width === 'full'}
            $withoutPadding={withoutPadding}
          >
            {children}
          </ScrollableContent>
          {footerContent && (
            <Footer $fullViewport={width === 'full'}>{footerContent}</Footer>
          )}
        </DialogPanel>
      </Wrapper>
    </Dialog>
  );
};

export { Overlay };
